/**
 * When adding or removing flags, update the following:
 * - LDFlags enum
 * - if flag is not a boolean, add/remove the flag to the <type>Flags array
 */

export enum LDFlags {
  // ----- Active flags ----- //
  // Some of these we want to keep as a just in case
  COM_PRIMARY_ADDRESS_AUTOFILL = 'com-primary-address-autofill',
  COM_CANADA_EXPERIENCE = 'com-canada-experience',
  COM_REFERRAL_PROGRAM = 'com-referral-program',
  KEEP_MY_CAR_SELECTOR = 'keep-my-car-selector',
  MARKETPLACE_BLOCKED_LIENHOLDERS = 'marketplace-blocked-lienholders',
  METHODFI_SEPARATE_IDENTITY_CALL = 'methodfi-separate-identity-call',
  PAYOFF_INTELLIGENCE_ENABLED = 'payoff-intelligence-enabled',
  SERVICE_DOWN_BANNER = 'service-down-banner',

  // ----- Temporary flags ----- //
  // Flags meant to be removed after A/B test or implementation
  AUTO_POPULATE_EMPLOYER_PHONE = 'auto-populate-employer-phone',
  CALIFORNIA_DATA_SHARING_CONSENT = 'california-data-sharing-consent',
  GLBA_PRIVACY_NOTICE = 'glba-privacy-notice',
  AFTER_HOURS_SELF_SERVICE_PAYOFF = 'after-hours-self-service-payoff',
  HIDE_GETTING_STARTED_BACK_BUTTON = 'hide-getting-started-back-button',
  COM_HIDE_PAYOFF_PAGE = 'com-hide-payoff-page',
  COM_PAYOFF_INTELLIGENCE_DASHBOARD_PANEL = 'com-payoff-intelligence-dashboard-panel',
  COM_PRIVACY_GTM_GATE = 'com-privacy-gtm-gate',
  COM_PRIVACY_OPTOUT_LOOKUP = 'com-privacy-optout-lookup',
  COM_PRIVACY_RUDDER_CONSENT = 'com-privacy-rudder-consent',
  PAYOFF_INTELLIGENCE_STRESS_TEST = 'payoff-intelligence-stress-test',
  PASSWORDLESS_DASHBOARD_ACCESS = 'passwordless-dashboard-access',
  SHOW_FLOW_PROGRESS_METER = 'show-flow-progress-meter',
  SHOW_PAGE_PROGRESS_METER = 'show-page-progress-meter',
  HIDE_MARKETPLACE_LIST_PRICE = 'hide-marketplace-list-price',
  MARKETPLACE_DASHBOARD = 'marketplace-dashboard',
  MARKETPLACE_DELIST = 'marketplace-delist',
  MARKETPLACE_FLOW_PIVOT = 'marketplace-flow-pivot',
  MARKETPLACE_PHONE_VERIFICATION = 'marketplace-phone-verification-enabled',
  MARKETPLACE_OFFERS_ENABLED = 'marketplace-offers-enabled',
  MARKETPLACE_MESSAGING = 'marketplace-messaging',
  MARKETPLACE_POST_ACCEPTANCE_ENABLED = 'marketplace-post-acceptance-enabled',
  MARKETPLACE_PRELIST_PHOTOS_AND_ID = 'marketplace-prelist-photos-and-id',
  MARKETPLACE_ALMOST_LIVE = 'marketplace-almost-live',
  MARKETPLACE_SELLER_CONFIDENCE = 'marketplace-seller-confidence',
  MARKETPLACE_FAQ = 'marketplace-faq',
  MARKETPLACE_EXPLAINER_VIDEO_PLACEMENT = 'marketplace-explainer-video-placement',
  DISABLE_MARKETPLACE_SELF_SERVE = 'disable-marketplace-self-serve',
  COM_POST_SIGNING_STEPPER_ENABLED = 'com-post-signing-stepper-enabled',

  // ----- Ready to remove ----- //

  PHONE_NUMBER_PLACEMENT_VARIATION = 'phone-number-placement-variation',
  SECURE_INFO_LINK = 'secure-info-link',
  SHOW_EMAIL_NOTE = 'show-email-note',
  SHOW_PAYOFF_ESTIMATED_MODAL = 'show-payoff-estimated-modal',
  STACKED_RADIO_BUTTONS = 'stacked-radio-buttons',

  RE_CAPTCHA_ENABLED = 're-captcha-enabled', // Marketing mixed

  // Not referenced but needs cleanup
  FLOW_SHOPPING_CART = 'flow-shopping-cart',
}

// Flag evaluations we don't want tracked in Rudderstack
export const FLAG_TRACKING_BLACK_LIST = [
  LDFlags.RE_CAPTCHA_ENABLED,
  LDFlags.COM_PRIVACY_GTM_GATE,
  LDFlags.COM_PRIVACY_OPTOUT_LOOKUP,
  LDFlags.COM_PRIVACY_RUDDER_CONSENT,
  LDFlags.MARKETPLACE_BLOCKED_LIENHOLDERS,
];

const stringFlags = [
  LDFlags.SERVICE_DOWN_BANNER,
  LDFlags.MARKETPLACE_EXPLAINER_VIDEO_PLACEMENT,
] as const;
const numberFlags = [LDFlags.PHONE_NUMBER_PLACEMENT_VARIATION] as const;
const stringArrayFlags = [LDFlags.MARKETPLACE_BLOCKED_LIENHOLDERS] as const;

export type LDFlagValueTypes = {
  [key in LDFlags]: key extends typeof stringFlags[number]
    ? string
    : key extends typeof numberFlags[number]
    ? number
    : key extends typeof stringArrayFlags[number]
    ? string[]
    : boolean;
};

export type FlagOverrides = {
  [key in LDFlags]?: LDFlagValueTypes[key];
};
